import React, { useState, useEffect } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchContractStatus } from "../../Redux/userSlice";
import api from "../services/influancerService";
import { AlertTriangle } from "lucide-react";
import Swal from "sweetalert2";
import LoadingSpinner from "../../components/LoadingSpinner";
import { INFLUENCER_BASE } from "../constants";

const inputClass =
  "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100";

const AccountDetailsForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const contractStatus = useSelector((state) => state.user.contractStatus);

  const [formData, setFormData] = useState({
    name: "",
    bankAccount: "",
    reEnterBankAccount: "",
    accountType: "",
    ifscCode: "",
    bankName: "",
    bankBranch: "",
    panDetails: "",
  });

  const [existingBankDetails, setExistingBankDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [panCardFile, setPanCardFile] = useState(null);
  const [panCardPreview, setPanCardPreview] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        await dispatch(fetchContractStatus()).unwrap();
      } catch (error) {
        console.error("Error fetching contract status:", error);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Unable to fetch contract status. Please try again later!",
        });
      }
    };

    fetchStatus();
  }, [dispatch]);

  useEffect(() => {
    const fetchBankDetails = async () => {
      try {
        const response = await api.get("/auth/users/bank-details");
        if (response.data) {
          const data = response.data;
          setExistingBankDetails(data);
          setFormData({
            ...data,
            reEnterBankAccount: data.bankAccount || "",
            panCardImageUrl: data.panCardImage?.url || undefined,
          });
        }
      } catch (error) {
        console.error("Error fetching bank details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (contractStatus === "signed" || contractStatus === "in progress") {
      fetchBankDetails();
    }
  }, [contractStatus]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePanCardChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      Swal.fire({ icon: "warning", title: "Invalid file", text: "Please select an image (JPG, PNG, etc.)." });
      return;
    }
    setPanCardFile(file);
    const reader = new FileReader();
    reader.onload = () => setPanCardPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearPanCard = () => {
    setPanCardFile(null);
    setPanCardPreview(null);
    setFormData((prev) => ({ ...prev, panCardImageUrl: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.bankAccount !== formData.reEnterBankAccount) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Bank account numbers do not match!",
      });
      return;
    }

    if (!formData.name || !formData.bankAccount || !formData.ifscCode) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill out all required fields.",
      });
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = {
        name: formData.name,
        bankAccount: formData.bankAccount,
        accountType: formData.accountType,
        ifscCode: formData.ifscCode,
        bankName: formData.bankName,
        bankBranch: formData.bankBranch,
        panDetails: formData.panDetails || "",
      };

      if (panCardFile) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(panCardFile);
        });
        payload.panCardImage = base64;
      }

      await api.post("/auth/users/bank-details", payload);

      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Bank details saved successfully!",
        confirmButtonText: "OK",
      });

      const refetch = await api.get("/auth/users/bank-details");
      if (refetch.data) setExistingBankDetails(refetch.data);
      setPanCardFile(null);
      setPanCardPreview(null);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.response?.data?.message || "Error saving details.",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(`${INFLUENCER_BASE}/dashboard`);
  };

  if (contractStatus === "not signed" || contractStatus === "not started") {
    return (
      <div className="w-full p-4 lg:p-6">
        <button
          type="button"
          onClick={handleBackClick}
          className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600"
        >
          <FaArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
        <div className="mx-auto max-w-lg rounded-2xl border border-red-100 bg-white p-6 text-center shadow-sm sm:p-8">
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-red-500" />
          <h2 className="text-lg font-bold text-gray-900">Action Required</h2>
          <p className="mt-2 text-sm text-gray-500">
            Your contract status is <span className="font-semibold text-red-500">Not Signed</span> or{" "}
            <span className="font-semibold text-red-500">Not Started</span>.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Please get in touch with the <span className="font-semibold text-orange-600">Infuber Team</span> for more details.
          </p>
          <button
            type="button"
            onClick={() => navigate("/contact-details")}
            className="mt-5 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            Contact Support
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 lg:p-6">
      <button
        type="button"
        onClick={handleBackClick}
        className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600 lg:mb-4"
      >
        <FaArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>

      <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
        <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">
          {existingBankDetails ? "Your Bank Details" : "Add Bank Details"}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          <span className="font-medium text-orange-600">Please ensure</span> you enter the correct bank details here.
        </p>
      </div>

      <div className="mx-auto w-full max-w-2xl rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        {isLoading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <LoadingSpinner message="Loading bank details..." />
          </div>
        ) : contractStatus === "signed" || contractStatus === "in progress" ? (
          existingBankDetails ? (
            <div>
              <h2 className="mb-4 text-sm font-bold text-gray-900 sm:text-base">Current Bank Details</h2>
              <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 bg-gray-50 px-4">
                <DetailRow label="Account Name" value={existingBankDetails.name} />
                <DetailRow label="Bank Account" value={existingBankDetails.bankAccount} />
                <DetailRow label="Account Type" value={existingBankDetails.accountType} />
                <DetailRow label="IFSC Code" value={existingBankDetails.ifscCode} />
                <DetailRow label="Bank Name" value={existingBankDetails.bankName} />
                <DetailRow label="Bank Branch" value={existingBankDetails.bankBranch} />
                {existingBankDetails.panDetails && (
                  <DetailRow label="PAN Number" value={existingBankDetails.panDetails} />
                )}
              </div>
              {existingBankDetails.panCardImage?.url && (
                <div className="mt-4">
                  <p className="mb-2 text-xs font-semibold text-gray-500">PAN Card</p>
                  <div className="inline-block overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <img
                      src={existingBankDetails.panCardImage.url}
                      alt="PAN Card"
                      className="max-h-40 object-contain"
                    />
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  setExistingBankDetails(null);
                  setFormData((prev) => ({ ...prev, reEnterBankAccount: prev.bankAccount || "" }));
                }}
                className="mt-5 w-full rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
              >
                Update Details
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Field label="Account Name">
                <input type="text" name="name" value={formData.name} onChange={handleChange} className={inputClass} placeholder="Please enter your name here" />
              </Field>
              <Field label="Bank Account Number">
                <input type="text" name="bankAccount" value={formData.bankAccount} onChange={handleChange} className={inputClass} placeholder="Please enter bank account number here" />
              </Field>
              <Field label="Re-enter Bank Account Number">
                <input type="text" name="reEnterBankAccount" value={formData.reEnterBankAccount} onChange={handleChange} className={inputClass} placeholder="Please re-enter bank account number here" />
              </Field>
              <Field label="Account Type">
                <select name="accountType" value={formData.accountType} onChange={handleChange} className={inputClass}>
                  <option value="">Select Account Type</option>
                  <option value="saving">Saving</option>
                  <option value="current">Current</option>
                </select>
              </Field>
              <Field label="IFSC Code">
                <input type="text" name="ifscCode" value={formData.ifscCode} onChange={handleChange} className={inputClass} placeholder="Please enter IFSC Code here" />
              </Field>
              <Field label="Bank Name">
                <input type="text" name="bankName" value={formData.bankName} onChange={handleChange} className={inputClass} placeholder="Please enter Bank Name here" />
              </Field>
              <Field label="Bank Branch">
                <input type="text" name="bankBranch" value={formData.bankBranch} onChange={handleChange} className={inputClass} placeholder="Please enter Bank Branch here" />
              </Field>
              <Field label="PAN Number">
                <input type="text" name="panDetails" value={formData.panDetails || ""} onChange={handleChange} className={inputClass} placeholder="e.g. ABCDE1234F" maxLength={10} />
              </Field>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:text-sm">PAN Card Image</label>
                <p className="mb-2 text-xs text-gray-500">Upload a clear image of your PAN card (optional when updating).</p>
                <label className="flex w-full cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-gray-200 px-4 py-4 text-sm font-medium text-gray-600 transition hover:border-orange-300 hover:bg-orange-50">
                  <input type="file" accept="image/*" className="hidden" onChange={handlePanCardChange} />
                  Choose image
                </label>
                {(panCardPreview || formData.panCardImageUrl) && (
                  <div className="relative mt-3 inline-block overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                    <img src={panCardPreview || formData.panCardImageUrl} alt="PAN preview" className="max-h-44 object-contain" />
                    <button
                      type="button"
                      onClick={clearPanCard}
                      className="absolute right-2 top-2 rounded-full bg-red-500 px-1.5 text-sm leading-none text-white hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={submitLoading}
                className="w-full rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitLoading ? "Saving..." : "Save Details"}
              </button>
            </form>
          )
        ) : (
          <div className="flex min-h-[200px] flex-col items-center justify-center text-center">
            <AlertTriangle className="mb-3 h-10 w-10 text-red-500" />
            <p className="text-sm text-gray-500">Please get in touch with the Infuber team for more details.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:text-sm">{label}</label>
    {children}
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 py-3 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium capitalize text-gray-900">{value}</span>
  </div>
);

export default AccountDetailsForm;
